import { useCallback, useEffect, useMemo, useState } from 'react';
import type { Repositories } from '@core/db';
import type { CaseEvent, EventType } from '@core/domain/model';
import { fullDateTime } from '@core/ui/RuDates';
import { eventTypeLabel } from './month';

/**
 * Карточка события — «Структура» §9.2.
 *
 * Задача открывается своей карточкой в разделе «Задачи»: у неё есть отметка выполнения
 * и срок, а у заседания их нет. Здесь живут заседания, встречи и «другое».
 */
export interface EventDetailState {
  isLoading: boolean;
  notFound: boolean;
  title: string;
  type: EventType;
  typeLabel: string;
  /** «14 сентября 2026, 10:30» */
  whenLabel: string;
  startAt: number;
  description: string;
  caseId: string | null;
  caseTitle: string | null;
  /** Клиент дела: позвонить перед заседанием, не разыскивая его через дело. */
  clientId: string | null;
  clientName: string | null;
  cases: { id: string; title: string }[];
  /** Событие удалено и ждёт «Отменить» — [D-12](docs/DECISIONS.md). */
  isDeleted: boolean;
}

export function useEventDetail(eventId: string, db: Repositories) {
  const [event, setEvent] = useState<CaseEvent | null>(null);
  const [cases, setCases] = useState<{ id: string; title: string }[]>([]);
  const [caseTitle, setCaseTitle] = useState<string | null>(null);
  const [client, setClient] = useState<{ id: string; name: string } | null>(null);
  const [isLoading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);
  const [isDeleted, setDeleted] = useState(false);
  const [version, setVersion] = useState(0);

  const refresh = useCallback(() => setVersion((value) => value + 1), []);

  useEffect(() => {
    let dropped = false;

    void (async () => {
      const [found, allCases, allClients] = await Promise.all([
        db.events.find(eventId),
        db.cases.list({}),
        db.clients.list(),
      ]);
      if (dropped) return;

      if (!found) {
        setNotFound(true);
        setLoading(false);
        return;
      }

      setEvent(found);

      const owner = allCases.find((item) => item.id === found.caseId) ?? null;
      const person = allClients.find((item) => item.id === owner?.clientId) ?? null;

      setCaseTitle(owner?.title ?? null);
      setClient(person ? { id: person.id, name: person.fullName } : null);
      setCases(allCases.map((item) => ({ id: item.id, title: item.title })));
      setLoading(false);
    })();

    return () => {
      dropped = true;
    };
  }, [db, eventId, version]);

  const state: EventDetailState = useMemo(
    () => ({
      isLoading,
      notFound,
      title: event?.title ?? '',
      type: event?.type ?? 'Hearing',
      typeLabel: event ? eventTypeLabel(event.type) : '',
      whenLabel: event ? fullDateTime(new Date(event.startAt)) : '',
      startAt: event?.startAt ?? 0,
      description: event?.description ?? '',
      caseId: event?.caseId ?? null,
      caseTitle,
      clientId: client?.id ?? null,
      clientName: client?.name ?? null,
      cases,
      isDeleted,
    }),
    [event, caseTitle, client, cases, isLoading, notFound, isDeleted],
  );

  const edit = useCallback(
    async (change: (current: CaseEvent) => CaseEvent) => {
      if (!event) return;

      await db.events.update(change(event));
      refresh();
    },
    [db, event, refresh],
  );

  const actions = useMemo(
    () => ({
      setTitle: (title: string) =>
        title.trim().length > 0 ? void edit((current) => ({ ...current, title })) : undefined,

      setDescription: (description: string) =>
        void edit((current) => ({ ...current, description: description.trim() || null })),

      setType: (type: EventType) =>
        void edit((current) => ({
          ...current,
          type,
          // Задача живёт сроком: сменив тип на неё, срок берём из времени начала,
          // иначе событие пропало бы из «Задач», куда только что переехало.
          deadlineAt: type === 'Task' ? current.startAt : null,
        })),

      setStart: (startAt: number) =>
        void edit((current) => ({
          ...current,
          startAt,
          deadlineAt: current.type === 'Task' ? startAt : current.deadlineAt,
        })),

      setCase: (caseId: string | null) => void edit((current) => ({ ...current, caseId })),

      async remove() {
        if (!event) return;

        await db.events.remove(event.id);
        setDeleted(true);
      },

      async undoDelete() {
        if (!event) return;

        await db.events.restore(event.id);
        setDeleted(false);
        refresh();
      },

      deletionShown: () => setDeleted(false),
      refresh,
    }),
    [db, event, edit, refresh],
  );

  return { state, actions };
}

export type EventDetailActions = ReturnType<typeof useEventDetail>['actions'];
